import { ProgramQuestionType, UpsertProgramQuestions } from "@humanex/interfaces";
import { Box, Button, Input, Select } from "@humanex/ui/components";
import { toast } from "@humanex/ui/libs";
import { createFileRoute, useParams, useRouteContext } from "@tanstack/react-router";
import { useState } from "react";

import { useUpsertProgramQuestions } from "@/hooks/queries/use-program-query";

export const Route = createFileRoute("/_authenticated/org/$orgId/program/$programId/questions/")({
  component: RouteComponent
});

interface QuestionDraft {
  id?: string;
  type: ProgramQuestionType;
  label: string;
  options: string[];
  required: boolean;
  order: number;
}

const QUESTION_TYPE_OPTIONS = [
  { value: ProgramQuestionType.TEXT, label: "Văn bản tự do" },
  { value: ProgramQuestionType.SINGLE_CHOICE, label: "Một lựa chọn" },
  { value: ProgramQuestionType.MULTIPLE_CHOICE, label: "Nhiều lựa chọn" },
  { value: ProgramQuestionType.FILE_UPLOAD, label: "Tải file" },
  { value: ProgramQuestionType.PROFILE_FIELD, label: "Trường hồ sơ" }
];

const CHOICE_TYPES = new Set<ProgramQuestionType>([
  ProgramQuestionType.SINGLE_CHOICE,
  ProgramQuestionType.MULTIPLE_CHOICE
]);

function RouteComponent() {
  const { programId } = useParams({
    from: "/_authenticated/org/$orgId/program/$programId/questions/"
  });
  const { program } = useRouteContext({
    from: "/_authenticated/org/$orgId/program/$programId/questions/"
  });

  const [questions, setQuestions] = useState<QuestionDraft[]>(() =>
    (program.questions ?? []).map((q, i) => ({
      id: q.id,
      type: q.type as ProgramQuestionType,
      label: q.label,
      options: q.options ?? [],
      required: q.required,
      order: q.order ?? i
    }))
  );

  const { mutateAsync: upsertQuestions, isPending } = useUpsertProgramQuestions();

  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        type: ProgramQuestionType.TEXT,
        label: "",
        options: [],
        required: true,
        order: prev.length
      }
    ]);
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i })));
  };

  const moveQuestion = (index: number, direction: "up" | "down") => {
    const next = [...questions];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= next.length) return;
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    setQuestions(next.map((q, i) => ({ ...q, order: i })));
  };

  const updateQuestion = (index: number, patch: Partial<QuestionDraft>) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  };

  const updateOption = (qIndex: number, optIndex: number, value: string) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex) return q;
        const newOptions = [...q.options];
        newOptions[optIndex] = value;
        return { ...q, options: newOptions };
      })
    );
  };

  const addOption = (qIndex: number) => {
    setQuestions((prev) => prev.map((q, i) => (i === qIndex ? { ...q, options: [...q.options, ""] } : q)));
  };

  const removeOption = (qIndex: number, optIndex: number) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === qIndex ? { ...q, options: q.options.filter((_, oi) => oi !== optIndex) } : q))
    );
  };

  const handleSave = async () => {
    for (const q of questions) {
      if (!q.label.trim()) {
        toast.error("Vui lòng điền tiêu đề cho tất cả câu hỏi");
        return;
      }
    }

    const payload: UpsertProgramQuestions = {
      questions: questions.map((q) => ({
        ...(q.id ? { id: q.id } : {}),
        type: q.type,
        label: q.label.trim(),
        options: q.options.filter((o) => o.trim() !== ""),
        required: q.required,
        order: q.order
      }))
    };

    try {
      await upsertQuestions({ id: programId, data: payload });
      toast.success("Đã lưu câu hỏi");
    } catch {
      toast.error("Có lỗi khi lưu câu hỏi");
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-responsive-xl font-normal text-text-primary">Câu hỏi bổ sung</p>
          <p className="text-responsive-base text-text-secondary mt-1">{program.title}</p>
        </div>
        <Button variant="default" onClick={handleSave} disabled={isPending}>
          {isPending ? "Đang lưu..." : "Lưu câu hỏi"}
        </Button>
      </div>

      <div className="space-y-4">
        {questions.length === 0 && (
          <Box className="py-8 text-center">
            <p className="text-text-secondary">Chưa có câu hỏi nào. Nhấn "Thêm câu hỏi" để bắt đầu.</p>
          </Box>
        )}

        {questions.map((q, index) => (
          <Box key={index} className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-text-primary font-medium text-sm">Câu hỏi {index + 1}</p>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  padding="px-2 py-1"
                  onClick={() => moveQuestion(index, "up")}
                  disabled={index === 0}
                >
                  ↑
                </Button>
                <Button
                  variant="ghost"
                  padding="px-2 py-1"
                  onClick={() => moveQuestion(index, "down")}
                  disabled={index === questions.length - 1}
                >
                  ↓
                </Button>
                <Button
                  variant="ghost"
                  padding="px-2 py-1"
                  className="text-red-500 hover:text-red-600"
                  onClick={() => removeQuestion(index)}
                >
                  Xóa
                </Button>
              </div>
            </div>

            <Select
              label="Loại câu hỏi"
              value={q.type}
              onChange={(e) =>
                updateQuestion(index, {
                  type: e.target.value as ProgramQuestionType,
                  options: []
                })
              }
              options={QUESTION_TYPE_OPTIONS}
            />

            <Input
              label="Tiêu đề câu hỏi"
              value={q.label}
              onChange={(e) => updateQuestion(index, { label: e.target.value })}
              placeholder="Nhập tiêu đề câu hỏi"
              required
            />

            <div className="flex items-center gap-3">
              <input
                id={`required-${index}`}
                type="checkbox"
                checked={q.required}
                onChange={(e) => updateQuestion(index, { required: e.target.checked })}
                className="w-4 h-4 rounded border border-border accent-text-primary"
              />
              <label htmlFor={`required-${index}`} className="text-sm text-text-primary">
                Bắt buộc
              </label>
            </div>

            {CHOICE_TYPES.has(q.type) && (
              <div className="space-y-2">
                <p className="text-text-secondary text-sm font-medium">Các lựa chọn</p>
                {q.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <Input
                      value={opt}
                      onChange={(e) => updateOption(index, oi, e.target.value)}
                      placeholder={`Lựa chọn ${oi + 1}`}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      padding="px-2 py-1"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => removeOption(index, oi)}
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                <Button variant="outline" padding="px-3 py-1" onClick={() => addOption(index)}>
                  + Thêm lựa chọn
                </Button>
              </div>
            )}
          </Box>
        ))}
      </div>

      <Button variant="outline" onClick={addQuestion}>
        + Thêm câu hỏi
      </Button>
    </div>
  );
}
